<template>
  <div>
    <div
      v-if="!showImportModal"
      class="modal-shadow saved_popup min-h-screen flex flex-col"
      @click.self="cancelPick"
    >
      <div class="inner">
        <div class="content">
          <div class="w-full flex justify-between modal_header p-6 title_line">
            <div class="modal_title mt-2">Upload drivers CSV</div>
            <button type="button" class="x_btn" @click="cancelPick">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
            </button>
          </div>

          <div class="modal_body flex flex-col items-center px-6 pb-6 pt-2">
            <p class="text-grey-dark text-sm mt-2 max-w-3xl mx-auto text-center">
              <a
                :href="driversImportTemplatePath"
                :download="driversImportTemplateFilename"
                class="text-primary underline"
              >Download template</a>
              for the driver import format. Each row needs driver name, username, and password. Mobile phone and address columns are optional.
            </p>
            <input
              accept=".csv"
              ref="uploadCSVFile"
              type="file"
              hidden
              @change="fileUploaded()"
            >
            <div class="mt-6 flex flex-col items-center gap-3">
              <button
                type="button"
                class="btn green-white px-6"
                @click="openFilePicker"
              >
                Choose CSV file
              </button>
              <span class="text-grey-dark text-sm">
                {{ selectedFileName || 'No file chosen' }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <DriversCsvImportModal
      v-if="showImportModal && importRows.length"
      :rows="importRows"
      @finished="finishImportFlow"
    />
  </div>
</template>

<script>
import parseFile from 'vue-xls-csv-parser/src/parser';
import {
  DRIVERS_IMPORT_TEMPLATE_FILENAME,
  DRIVERS_IMPORT_TEMPLATE_PATH,
} from '@/constants/driversImport';
import DriversCsvImportModal from '@/components/DriversCsvImportModal';
import { infoMessage } from '@/services/messages';

function normalizeHeader(header) {
  let key = String(header || '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');
  if (key === 'name') {
    key = 'driver name';
  }
  if (key === 'username') {
    key = 'driver username';
  }
  if (key === 'password') {
    key = 'driver password';
  }
  return key;
}

const REQUIRED_HEADERS = ['driver name', 'driver username', 'driver password'];

export default {
  name: 'CSVUploadDrivers',
  inheritAttrs: false,
  components: {
    DriversCsvImportModal,
  },
  data() {
    return {
      driversImportTemplatePath: DRIVERS_IMPORT_TEMPLATE_PATH,
      driversImportTemplateFilename: DRIVERS_IMPORT_TEMPLATE_FILENAME,
      showImportModal: false,
      importRows: [],
      selectedFileName: '',
    };
  },
  methods: {
    cancelPick() {
      this.$emit('cancel');
    },
    openFilePicker() {
      this.$refs.uploadCSVFile?.click();
    },
    finishImportFlow() {
      this.$emit('import-complete');
    },
    isEmptyDataRow(values) {
      return !values.some((v) => String(v ?? '').trim() !== '');
    },
    fileJsonToRows(fileJson) {
      if (!fileJson?.length) {
        return [];
      }
      const columns = {};
      fileJson.forEach(({ name, data }) => {
        columns[name] = data;
      });
      const rowCount = fileJson[0].data.length;
      const rows = [];
      for (let i = 0; i < rowCount; i++) {
        const row = {};
        Object.keys(columns).forEach((name) => {
          row[name] = columns[name][i];
        });
        if (!this.isEmptyDataRow(Object.values(row))) {
          rows.push(row);
        }
      }
      return rows;
    },
    rowHasRequiredColumns(row) {
      const normalized = Object.keys(row || {}).map((h) => normalizeHeader(h));
      return REQUIRED_HEADERS.every((required) => normalized.includes(required));
    },
    async fileUploaded() {
      const file = this.$refs.uploadCSVFile?.files?.[0];
      if (!file) {
        this.selectedFileName = '';
        return;
      }
      this.selectedFileName = file.name;
      try {
        const fileJson = await parseFile(file, 'en');
        const rows = this.fileJsonToRows(fileJson);
        if (!rows.length) {
          infoMessage('No data rows found in CSV', 'error');
          return;
        }
        if (!this.rowHasRequiredColumns(rows[0])) {
          infoMessage(
            'CSV must include driver name, driver username, and driver password columns',
            'error',
          );
          return;
        }
        this.importRows = rows;
        this.showImportModal = true;
      } catch (error) {
        console.error(error);
        infoMessage('Could not parse CSV file', 'error');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.modal-shadow {
  position: fixed;
  top: 0;
  left: 0;
  min-height: 100%;
  width: 100%;
  background: rgba(0, 0, 0, 0.39);
  z-index: 1100;
}
.modal_header {
  border-radius: 8px 8px 0 0;
  border-bottom: none;
}
.saved_popup {
  position: fixed;
  right: 0;
  top: 0;
  bottom: 0;
  width: 100vw;
}
.inner {
  margin: 40px auto;
  max-width: 640px;
  background: #fff;
  border-radius: 8px;
}
</style>
